/* eslint-disable no-nested-ternary */
import styled from "@emotion/styled"
import Checkbox from "@mui/material/Checkbox"
import ListItemText from "@mui/material/ListItemText"
import MenuItem from "@mui/material/MenuItem"
import Select, { SelectChangeEvent } from "@mui/material/Select"
import { Dispatch, SetStateAction, useState } from "react"
import {
  FilterArrowIcon,
  FilterCheckbox,
  useIsDesktop,
  useIsLargeDesktop,
} from "ui"
import { CategoryState } from "./JobsSearch"

const StyledMenuItem = styled(MenuItem) <{ selected: boolean }>`
  padding: 0;
  padding-right: 6px;
  margin: 0;
  color: #153e69;
  span {
    font-weight: ${(props) => (props.selected ? 500 : 400)};
    font-size: 16px;
    line-height: 24px;
    ${(props) => props.theme.breakpoints.up("md")} {
      font-size: 12px;
      line-height: 16px;
    }
    ${(props) => props.theme.breakpoints.up("lg")} {
      font-size: 16px;
      line-height: 24px;
    }
  }

  border-bottom: 1px dashed black;
  background: ${(props) => (props.selected ? "#153E694D" : "transparent")};
`

const StyledSelect = styled(Select)`
  width: 100%;
  ${(props) => props.theme.breakpoints.up("md")} {
    width: 208px;
  }

  color: #153e69;
  ${(props) => props.theme.breakpoints.up("lg")} {
    width: 312px;
  }
  fieldset {
    border: none;
  }
  /* Add focus-visible rule */
 div[role="button"]:focus-visible {
    border: 1px solid black !important; /* Only when keyboard-focused */
  }
  div[role="button"] {
    display: flex;
    align-items: center;
    height: 24px;
    padding: 0;
    font-size: 16px;
    line-height: 24px;
    ${(props) => props.theme.breakpoints.up("md")} {
      font-size: 12px;
      line-height: 16px;
    }
    ${(props) => props.theme.breakpoints.up("lg")} {
      font-size: 16px;
      line-height: 24px;
    }
  }
  ${(props) => props.theme.breakpoints.up("md")} {
    .MuiOutlinedInput-input {
      padding: 0px;
    }
  }
`

type Props = {
  categoryState: CategoryState
  setCategoryState: Dispatch<SetStateAction<CategoryState>>
}

export default function DropBox({ categoryState, setCategoryState }: Props) {
  const [open, setOpen] = useState(false)
  const isDesktop = useIsDesktop()
  const isLargeDesktop = useIsLargeDesktop()
  const width = () => {
    if (isLargeDesktop) {
      return 353
    } else if (isDesktop) {
      return 235
    } else {
      return 303
    }
  }
  const handleSelectChange = (event: SelectChangeEvent<unknown>) => {
    const selected = event.target.value as string[]
    setCategoryState((prev) => {
      const next = { ...prev }
      Object.keys(next).forEach((name) => {
        next[name] = { ...next[name], checked: selected.includes(name) }
      })
      return next
    })
  }
  return (
    <StyledSelect
      sx={{
        borderRadius: "100px",
        height: "24px",
        border: "none",
        outline: "none",
      }}
      multiple
      value={Object.keys(categoryState).filter(
        (name) => categoryState[name].checked,
      )}
      onChange={handleSelectChange}
      displayEmpty
      onClose={() => setOpen(false)}
      onOpen={() => setOpen(true)}
      IconComponent={FilterArrowIcon}
      renderValue={() => "אני רוצה למצוא משרה ב..."}
      SelectDisplayProps={{
        "aria-label": "קטגוריה - תיבות סימון מרובות",
        "aria-haspopup": "listbox",
        "aria-expanded": open,
      }}
      MenuProps={{
        MenuListProps: { variant: 'menu', },
        PaperProps: {
          style: {
            minWidth: width(),
            borderRadius: "10px",
            borderTopLeftRadius: 0,
            borderTopRightRadius: 0,
            padding: 0,
            border: "none",
          },
          sx: {
            ".MuiList-root": {
              paddingTop: 0,
              paddingBottom: "28px",
            },
          },
        },
        anchorOrigin: {
          horizontal: "center",
          vertical: "bottom",
        },
        transformOrigin: {
          vertical: isLargeDesktop ? -25 : isDesktop ? -10 : -4,
          horizontal: "center",
        },
      }}
    >
      {Object.entries(categoryState).map(([name, { checked, id }]) => (
        <StyledMenuItem
          key={id}
          value={name}
          selected={checked}
          role="menuitemcheckbox"
          aria-checked={checked}
        >
          <Checkbox
            icon={<FilterCheckbox role="presentation" checked={checked} />}
            checkedIcon={<FilterCheckbox role="presentation" checked={checked} />}
            checked={checked}
            role="presentation"
            inputProps={{ role: "presentation" }}
            tabIndex={-1}
          />
          <ListItemText primary={name} />
        </StyledMenuItem>
      ))}
    </StyledSelect>
  )
}
